import React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Skeleton,
  Backdrop,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// ─── AlreadySubmittedScreen ───────────────────────────────────────────────────
export const AlreadySubmittedScreen = React.memo(({ surveyTitle, onRetake, t }) => (
  <Container maxWidth="sm" sx={{ pt: 10, pb: 10 }}>
    <Paper elevation={3} sx={{ p: 6, borderRadius: 3, textAlign: 'center' }}>
      <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t.alreadySubmittedTitle || '¡Ya respondiste esta encuesta!'}
      </Typography>
      {surveyTitle && (
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
          {surveyTitle}
        </Typography>
      )}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t.alreadySubmittedMessage ||
          'Ya enviaste tus respuestas anteriormente. Gracias por tu participación.'}
      </Typography>
      <Button
        variant="outlined"
        color="primary"
        onClick={onRetake}
        sx={{ borderRadius: 2, px: 4, py: 1.2 }}
      >
        {t.retakeSurvey || 'Responder de nuevo'}
      </Button>
    </Paper>
  </Container>
));
AlreadySubmittedScreen.displayName = 'AlreadySubmittedScreen';

// ─── SurveySkeleton ───────────────────────────────────────────────────────────
export const SurveySkeleton = React.memo(() => (
  <Container maxWidth="md" className="main-container">
    <Paper elevation={3} className="survey-paper">
      <Box className="survey-title-skeleton">
        <Skeleton
          variant="text"
          width="60%"
          height={60}
          style={{ margin: '0 auto', marginBottom: '3rem' }}
        />
      </Box>
      <Skeleton
        variant="text"
        width="80%"
        height={30}
        style={{ margin: '0 auto', marginBottom: '4rem' }}
      />
      <Box className="progress-container">
        <Box className="progress-labels">
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="text" width={30} height={20} />
        </Box>
        <Skeleton variant="rounded" width="100%" height={8} />
      </Box>

      {[
        <Box className="rating-container" style={{ flexDirection: 'row', justifyContent: 'flex-start' }}>
          {[1, 2, 3, 4, 5].map((s) => (
            <Skeleton key={s} variant="circular" width={30} height={30} style={{ margin: '0 4px' }} />
          ))}
          <Skeleton variant="text" width={80} height={20} style={{ marginLeft: '2rem' }} />
        </Box>,
        <Box className="slider-container">
          <Skeleton variant="rounded" width="100%" height={20} />
          <Box className="slider-labels" style={{ marginTop: '1rem' }}>
            <Skeleton variant="text" width={40} height={16} />
            <Skeleton variant="text" width={40} height={16} />
          </Box>
        </Box>,
        <Box className="radio-group" style={{ marginTop: '2rem' }}>
          {[1, 2, 3].map((o) => (
            <Box key={o} style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <Skeleton variant="circular" width={20} height={20} style={{ marginRight: '12px' }} />
              <Skeleton variant="text" width={150} height={20} />
            </Box>
          ))}
        </Box>,
        <Box className="emoji-container">
          <Box
            className="emoji-list"
            style={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'center', gap: 8 }}
          >
            {[1, 2, 3, 4, 5].map((e) => (
              <Skeleton
                key={e}
                variant="circular"
                style={{ width: 'clamp(38px, 13vw, 82px)', height: 'clamp(38px, 13vw, 82px)', flex: '1 1 0' }}
              />
            ))}
          </Box>
          <Box className="emoji-labels" style={{ marginTop: '1rem' }}>
            <Skeleton variant="text" width={50} height={16} />
            <Skeleton variant="text" width={60} height={16} />
          </Box>
        </Box>,
      ].map((content, i) => (
        <Card key={i} variant="outlined" className="question-card">
          <CardContent>
            <Skeleton variant="text" width="70%" height={32} style={{ marginBottom: '1rem' }} />
            {content}
          </CardContent>
        </Card>
      ))}

      <Box className="submit-button-container">
        <Skeleton variant="rounded" width={200} height={50} />
      </Box>
    </Paper>
  </Container>
));
SurveySkeleton.displayName = 'SurveySkeleton';

// ─── SubmittingOverlay ─────────────────────────────────────────────────────────
export const SubmittingOverlay = React.memo(({ open, message }) => (
  <Backdrop
    open={open}
    sx={{
      color: '#fff',
      zIndex: (theme) => theme.zIndex.drawer + 1,
      background: 'rgba(0,0,0,0.55)',
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <CircularProgress color="inherit" size={56} />
      <Typography variant="h6" sx={{ color: 'white', textAlign: 'center', px: 2 }}>
        {message}
      </Typography>
    </Box>
  </Backdrop>
));
SubmittingOverlay.displayName = 'SubmittingOverlay';

// ─── PreparingResultScreen ─────────────────────────────────────────────────────
export const PreparingResultScreen = React.memo(({ willShowResult, t }) => (
  <Container maxWidth="sm" sx={{ pt: 10, pb: 10 }}>
    <Paper elevation={3} sx={{ p: 6, borderRadius: 3, textAlign: 'center' }}>
      <CircularProgress size={64} sx={{ mb: 3 }} />
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {willShowResult
          ? (t.calculatingResult || 'Saving your answers...')
          : (t.finishingUp || 'Just a moment, please...')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t.pleaseWait || 'Please wait a few seconds.'}
      </Typography>
    </Paper>
  </Container>
));
PreparingResultScreen.displayName = 'PreparingResultScreen';